import axios from 'axios'
import { isNullish } from 'remeda'

interface HandleErrorOptions {
  /**
   * A custom message to be displayed or thrown before the error cause.
   */
  message?: string
  /**
   * Regulates if the passed error should be printed or re-thrown.
   */
  behaviour?: 'print' | 'throw'
}

/**
 * Handles errors by printing or re-throwing them, then logs them to external third-paty services. Also returns the error.
 *
 * @param value - The error value, usually spawned by a catch cause or thrown.
 */
export const handleError = (
  value: unknown,
  {
    message = 'Something went wrong.',
    behaviour = 'print',
  }: HandleErrorOptions = {
    message: 'Something went wrong.',
    behaviour: 'print',
  },
): Error | undefined => {
  if (isNullish(value)) {
    return
  }

  const outputMessage = axios.isAxiosError(value)
    ? `Message: ${message} \n ${JSON.stringify(value, null, 2)}`
    : message

  if (value instanceof Error) {
    if (behaviour === 'print') {
      console.error('Error:', outputMessage, value)
    }

    if (behaviour === 'throw') {
      throw new Error(outputMessage, { cause: value })
    }

    // Special error capturing logic, manually sending events to log ingestion systems etc...

    return value
  }

  let stringifiedValue = 'Unable to stringify the thrown value.'

  try {
    stringifiedValue = JSON.stringify(value)
  } catch (stringifyError) {
    console.error(stringifiedValue, stringifyError)
  }

  const error = new Error(
    `This value was thrown as is, not through an Error: ${stringifiedValue}`,
  )

  if (behaviour === 'print') {
    console.error('Error:', outputMessage, error)
  }

  if (behaviour === 'throw') {
    throw new Error(outputMessage, { cause: error })
  }

  // Special error capturing logic, manually sending events to log ingestion systems etc...

  return error
}
